import { BBOT_DJANGO_SERVER } from "Config";
import AdminStaffAlerts from "pages/support-alerts/AdminStaffAlerts";
import FraudContacts from "pages/support-alerts/FraudContacts";
import StaffAlerts from "pages/support-alerts/StaffAlerts";
import TerminalAlerts from "pages/support-alerts/TerminalAlerts";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { BbotAlert } from "top-component-library";
import BbotTabs from "top-component-library/BbotTabs";
import OPContainer from "top-component-library/OPContainer";

import { useSupportAlerts } from "./useSupportAlerts";

export const SUPPORT_ALERTS_TAB_PARAM = "tab";

export const SUPPORT_ALERTS_TABS = {
  STAFF_ALERTS: "staff-alerts",
  TERMINAL_ALERTS: "terminal-alerts",
  FRAUD_ALERT_CONTACTS: "fraud-alert-contacts",
  ADMIN_STAFF_ALERTS: "admin-staff-alerts",
};

export const DEFAULT_SUPPORT_ALERTS_TAB = SUPPORT_ALERTS_TABS.STAFF_ALERTS;

const SupportAlerts = (props) => {
  const isAdmin = props.userInfo?.role === "admin";
  const legacyLink =
    BBOT_DJANGO_SERVER + (isAdmin ? "/superuser/console/#!/supportAlerts" : "/owner/console/#!/supportAlerts");
  const [hideTabHeaders, setHideTabHeaders] = useState(false);
  const { staffAlerts, terminalAlerts, adminStaffAlerts, fraudContacts, possibleStations, isLoading, refresh } =
    useSupportAlerts(props.selectedCustomer);

  const history = useHistory();
  const location = useLocation();

  // Admin Staff Alerts only exists as a pane for admins, so a non-admin's URL
  // can never resolve to it.
  const validTabs = useMemo(
    () => Object.values(SUPPORT_ALERTS_TABS).filter((tab) => isAdmin || tab !== SUPPORT_ALERTS_TABS.ADMIN_STAFF_ALERTS),
    [isAdmin]
  );

  // The URL is the only source of truth for the open tab, matching System Logs:
  // a link or a refresh lands on the same tab the sender was looking at, and an
  // unrecognised or inaccessible slug falls back to the default instead of
  // leaving AntD on a key that matches no pane.
  const activeKey = useMemo(() => {
    const requested = new URLSearchParams(location.search).get(SUPPORT_ALERTS_TAB_PARAM);
    return validTabs.includes(requested) ? requested : DEFAULT_SUPPORT_ALERTS_TAB;
  }, [location.search, validTabs]);

  // replace, not push: clicking through every tab should not bury the page the
  // user arrived from under several history entries.
  const selectTab = useCallback(
    (key) => {
      const params = new URLSearchParams(location.search);
      params.set(SUPPORT_ALERTS_TAB_PARAM, key);
      history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    },
    [history, location.pathname, location.search]
  );

  return (
    <OPContainer selectedCustomer={props.selectedCustomer} title="Support Text Alerts" refreshOnCurrentPageClick={true}>
      {!hideTabHeaders && (
        <div className={"margin-bottom-4"}>
          <LegacyLinkAlert
            message={
              <span>
                We've updated our support alerts page! We'll retire the old page soon but you can view it{" "}
                <a href={legacyLink}>here.</a>
              </span>
            }
          />
        </div>
      )}
      <BbotTabs id="support-alerts" hideTabHeaders={hideTabHeaders} activeKey={activeKey} onChange={selectTab}>
        <BbotTabs.TabPane tab="Staff Alerts" key={SUPPORT_ALERTS_TABS.STAFF_ALERTS} id="staff-alerts">
          <div className={"padding-x-2"}>
            <StaffAlerts
              staffAlerts={staffAlerts}
              terminalAlerts={terminalAlerts}
              isLoading={isLoading}
              selectedCustomer={props.selectedCustomer}
              setTabHeaderVisibility={(isEditing) => setHideTabHeaders(isEditing)}
              possibleStations={possibleStations}
              refreshData={refresh}
            />
          </div>
        </BbotTabs.TabPane>
        <BbotTabs.TabPane tab="Terminal Alerts" key={SUPPORT_ALERTS_TABS.TERMINAL_ALERTS} id="terminal-alerts">
          <div className={"padding-x-2"}>
            <TerminalAlerts
              terminalAlerts={terminalAlerts}
              possibleStations={possibleStations}
              isLoading={isLoading}
              selectedCustomer={props.selectedCustomer}
              refreshData={refresh}
            />
          </div>
        </BbotTabs.TabPane>
        <BbotTabs.TabPane
          tab="Fraud Alert Contacts"
          key={SUPPORT_ALERTS_TABS.FRAUD_ALERT_CONTACTS}
          id="fraud-alert-contacts"
        >
          <div className={"padding-x-2"}>
            <FraudContacts
              fraudContacts={fraudContacts}
              isLoading={isLoading}
              selectedCustomer={props.selectedCustomer}
              refreshData={refresh}
            />
          </div>
        </BbotTabs.TabPane>
        {isAdmin && (
          <BbotTabs.TabPane
            tab="Admin Staff Alerts"
            key={SUPPORT_ALERTS_TABS.ADMIN_STAFF_ALERTS}
            id="admin-staff-alerts"
          >
            <div className={"padding-x-2"}>
              <AdminStaffAlerts
                adminStaffAlerts={adminStaffAlerts}
                isLoading={isLoading}
                selectedCustomer={props.selectedCustomer}
                refreshData={refresh}
              />
            </div>
          </BbotTabs.TabPane>
        )}
      </BbotTabs>
    </OPContainer>
  );
};

export default SupportAlerts;

const LegacyLinkAlert = styled(BbotAlert)`
  margin-bottom: 15px;
`;
